import { useState } from 'react';

const TaxonomyPanel = ({ taxonomyText, jsonData }) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedRules, setExpandedRules] = useState({});

  // Use jsonData if available, otherwise fall back to parsing taxonomyText
  const taxonomyData = jsonData || (taxonomyText ? (() => {
    try {
      return JSON.parse(taxonomyText);
    } catch (e) {
      return null;
    }
  })() : null);

  const toggleCategory = (categoryIndex) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex]
    }));
  };

  const toggleRule = (categoryIndex, ruleIndex) => {
    const key = `${categoryIndex}-${ruleIndex}`;
    setExpandedRules(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!taxonomyData || !taxonomyData.taxonomy) {
    return (
      <div style={{
        textAlign: "center",
        padding: "20px",
        color: "var(--text-light)",
        fontSize: "12px"
      }}>
        <div style={{ fontSize: "24px", marginBottom: "8px" }}>📋</div>
        <div>No taxonomy configured</div>
      </div>
    );
  }

  const renderTaxonomyCategories = () => {
    const categories = taxonomyData.taxonomy || [];
    
    return categories.map((category, categoryIndex) => (
      <div key={categoryIndex} style={{ 
        marginBottom: '8px',
        border: '1px solid #e9ecef',
        borderRadius: '4px',
        backgroundColor: 'white'
      }}>
        {/* Category Header - Clickable */}
        <div 
          onClick={() => toggleCategory(categoryIndex)}
          style={{ 
            padding: '8px',
            cursor: 'pointer',
            backgroundColor: expandedCategories[categoryIndex] ? '#e3f2fd' : '#f8f9fa',
            borderBottom: expandedCategories[categoryIndex] ? '1px solid #e9ecef' : 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px',
            fontWeight: 'bold'
          }}
        >
          <div style={{ color: '#495057' }}>
            📁 {category.category}
          </div>
          <span style={{ fontSize: '10px', color: '#6c757d' }}>
            {expandedCategories[categoryIndex] ? '▼' : '▶'} {category.rules?.length || 0} rules
          </span>
        </div>
        
        {/* Category Content - Show when expanded */}
        {expandedCategories[categoryIndex] && (
          <div style={{ padding: '8px' }}>
            {category.rules && category.rules.map((rule, ruleIndex) => {
              const ruleKey = `${categoryIndex}-${ruleIndex}`;
              return (
                <div key={ruleIndex} style={{ 
                  marginBottom: '6px',
                  border: '1px solid #dee2e6',
                  borderRadius: '3px',
                  backgroundColor: '#fff'
                }}>
                  {/* Rule Header - Clickable */}
                  <div
                    onClick={() => toggleRule(categoryIndex, ruleIndex)}
                    style={{
                      padding: '6px',
                      backgroundColor: expandedRules[ruleKey] ? '#fff3cd' : '#f1f3f4',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: expandedRules[ruleKey] ? '1px solid #dee2e6' : 'none',
                      fontSize: '11px'
                    }}
                  >
                    <div style={{ fontWeight: '500' }}>
                      📄 Rule {ruleIndex + 1}
                    </div>
                    <span style={{ fontSize: '9px' }}>
                      {expandedRules[ruleKey] ? '▼' : '▶'}
                    </span>
                  </div>
                  
                  {/* Rule Content - Show when expanded */}
                  {expandedRules[ruleKey] && (
                    <div style={{ padding: '6px', fontSize: '10px' }}>
                      {/* Conditions */}
                      {rule.conditions && rule.conditions.length > 0 && (
                        <div style={{ marginBottom: '6px' }}>
                          <strong style={{ color: '#495057' }}>Conditions:</strong>
                          <ul style={{ margin: '2px 0', paddingLeft: '15px' }}>
                            {rule.conditions.map((condition, condIndex) => (
                              <li key={condIndex} style={{ fontSize: '9px', marginBottom: '2px' }}>
                                <code style={{ fontSize: '8px', backgroundColor: '#f8f9fa', padding: '1px 2px', borderRadius: '2px' }}>
                                  {condition.field}
                                </code> 
                                <span style={{ margin: '0 3px', color: '#6c757d' }}>{condition.type}</span>
                                <code style={{ fontSize: '8px', backgroundColor: '#e9ecef', padding: '1px 2px', borderRadius: '2px' }}>
                                  {JSON.stringify(condition.value)}
                                </code>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Content */}
                      {rule.content && (
                        <div>
                          <strong style={{ color: '#495057' }}>Content:</strong>
                          <div style={{ 
                            marginTop: '3px',
                            padding: '4px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderRadius: '2px',
                            fontSize: '9px'
                          }}>
                            {rule.content['cooperation-partner'] && (
                              <div style={{ marginBottom: '2px' }}>
                                <strong>Partner:</strong> {rule.content['cooperation-partner']}
                              </div>
                            )}
                            {rule.content.link && (
                              <div style={{ marginBottom: '2px' }}>
                                <strong>Link:</strong> 
                                <a href={rule.content.link} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '4px', color: '#007bff' }}>
                                  {rule.content.link.length > 30 ? rule.content.link.substring(0, 30) + '...' : rule.content.link}
                                </a>
                              </div>
                            )}
                            {rule.content.text && (
                              <div style={{ marginBottom: '2px' }}>
                                <strong>Text:</strong> {rule.content.text.length > 100 ? rule.content.text.substring(0, 100) + '...' : rule.content.text}
                              </div>
                            )}
                            {rule.content.bonustip && (
                              <div style={{ marginBottom: '2px' }}>
                                <strong>Bonus:</strong> 
                                <a href={rule.content.bonustip} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '4px', color: '#007bff' }}>
                                  {rule.content.bonustip.length > 30 ? rule.content.bonustip.substring(0, 30) + '...' : rule.content.bonustip}
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    ));
  };

  const renderGlobalSettings = () => {
    if (!jsonData) return null;
    
    const settings = {
      'Max Offers': jsonData.max_offers,
      'Max Bonus Tips': jsonData.max_bonus_tips,
      'Use Case IDs': Array.isArray(jsonData.usecase_ids) ? jsonData.usecase_ids.join(', ') : jsonData.usecase_id
    };

    return (
      <div style={{ 
        marginBottom: '8px',
        padding: '6px',
        border: '1px solid #e3f2fd',
        borderRadius: '3px',
        backgroundColor: '#f3f8ff',
        fontSize: '10px'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#1565c0' }}>
          ⚙️ Settings
        </div>
        {Object.entries(settings).map(([key, value]) => (
          <div key={key} style={{ marginBottom: '2px' }}>
            <strong>{key}:</strong> {value !== undefined ? value : 'Not set'}
          </div>
        ))}
      </div>
    );
  };

  const renderPartnerPriority = () => {
    if (!jsonData?.partner_priority?.length) return null;
    
    return (
      <div style={{ 
        marginTop: '8px',
        padding: '6px',
        border: '1px solid #fff3cd',
        borderRadius: '3px',
        backgroundColor: '#fffbf0',
        fontSize: '10px'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '4px', color: '#856404' }}>
          🤝 Partner Priority
        </div>
        {jsonData.partner_priority.map((partner, index) => (
          <div key={index} style={{ marginBottom: '2px' }}>
            <strong>{partner['cooperation-partner']}</strong>: Priority {partner.priority}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ fontSize: '11px' }}>
      {renderGlobalSettings()}
      {renderTaxonomyCategories()}
      {renderPartnerPriority()}
    </div>
  );
};

export default TaxonomyPanel;
